/**
 * /login — Supabase email/password sign-in.
 *
 * Per `docs/frontend/035-frontend-app-shell-plan.md §8.1`:
 *   - email + password → `supabase.auth.signInWithPassword`;
 *   - on success the supabase session lives in localStorage and the
 *     MeContextProvider's auth state listener picks it up automatically;
 *   - the `?next=<path>` query param (set by AuthGate on 401) is honoured
 *     for post-login redirect.
 *
 * Visual upgrade (Task 040): the centred card layout has been replaced
 * by a split-screen `<AuthShell>` matching
 * `design_handoff_super_agency/screens/01-login.html` from the Task 040
 * design zip. Auth logic, supabase calls, env-not-configured warning,
 * loading + error states, and the `?next=` redirect are all preserved
 * verbatim.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../auth/supabase';
import { useMeContext } from '../context/MeContextProvider';
import { env } from '../env';
import { AuthShell } from '../components/auth/AuthShell';

export function LoginPage() {
  const { status } = useMeContext();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/app';
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If a session already exists, bounce to /app (or the requested `next`).
  useEffect(() => {
    if (status === 'ready') {
      navigate(next, { replace: true });
    }
  }, [status, navigate, next]);

  if (status === 'ready') {
    return <Navigate to={next} replace />;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError('أدخل البريد وكلمة المرور.');
      return;
    }
    if (!env.isConfigured) {
      setError('متغيرات البيئة Supabase غير مهيّأة. راجع ملف apps/web/.env.example.');
      return;
    }
    setSubmitting(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        setError('البريد أو كلمة المرور غير صحيحة.');
        return;
      }
      navigate(next, { replace: true });
    } catch {
      setError('حدث خطأ أثناء تسجيل الدخول. حاول مجدداً.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell>
      <div className="auth-eyebrow">مرحبًا بعودتك</div>
      <h1 className="auth-title">
        سجّل <em>دخولك</em>
      </h1>
      <p className="auth-sub">"كل عميل، كل حملة، في مكان واحد."</p>

      <form onSubmit={onSubmit} noValidate>
        {!env.isConfigured ? (
          <div className="banner banner-info" style={{ marginBottom: 16 }}>
            متغيرات Supabase غير مهيّأة محلياً. أضفها إلى{' '}
            <code style={{ fontFamily: 'var(--font-mono)' }}>apps/web/.env.local</code> ثم أعد
            التشغيل.
          </div>
        ) : null}
        {error ? (
          <div className="banner banner-error" style={{ marginBottom: 16 }}>
            {error}
          </div>
        ) : null}

        <div className="auth-field">
          <label className="auth-field-label" htmlFor="email">
            البريد الإلكتروني
          </label>
          <div className="auth-input-wrap">
            <input
              id="email"
              className="auth-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              placeholder="you@super-agency.com"
              disabled={submitting}
              autoComplete="username"
              dir="ltr"
            />
            <svg
              className="auth-input-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="m3 7 9 6 9-6" />
            </svg>
          </div>
        </div>

        <div className="auth-field">
          <label className="auth-field-label" htmlFor="password">
            كلمة المرور
          </label>
          <div className="auth-input-wrap">
            <input
              id="password"
              className="auth-input auth-input--has-eye"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              placeholder="••••••••••••"
              disabled={submitting}
              autoComplete="current-password"
              dir="ltr"
            />
            <svg
              className="auth-input-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <rect x="4" y="10" width="16" height="11" rx="2" />
              <path d="M8 10V7a4 4 0 1 1 8 0v3" />
            </svg>
            <button
              type="button"
              className="auth-input-eye"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
              aria-pressed={showPassword}
              disabled={submitting}
            >
              {showPassword ? (
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M3 3l18 18" />
                  <path d="M10.6 6.1A11.3 11.3 0 0 1 12 6c6 0 10 6 10 6a14.4 14.4 0 0 1-3 3.6" />
                  <path d="M6.7 6.7A14.5 14.5 0 0 0 2 12s4 6 10 6c1.6 0 3-.4 4.3-1" />
                  <circle cx="12" cy="12" r="2.5" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div style={{ textAlign: 'end', marginTop: 4, marginBottom: 8 }}>
          <Link
            to="/forgot-password"
            className="tiny"
            style={{ color: 'var(--muted)', textDecoration: 'none' }}
          >
            نسيت كلمة المرور؟
          </Link>
        </div>

        <button type="submit" className="auth-cta" disabled={submitting} style={{ marginTop: 8 }}>
          {submitting ? <span className="spinner" /> : null}
          <span>{submitting ? 'جاري التحقق…' : 'دخول إلى اللوحة'}</span>
          {!submitting ? (
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          ) : null}
        </button>

        <p className="tiny" style={{ textAlign: 'center', color: 'var(--muted)', marginTop: 22 }}>
          ليس لديك حساب وكالة؟{' '}
          <Link to="/signup/agency" style={{ fontWeight: 600 }}>
            أنشئ حساب وكالة جديد
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
